import React, { useState, useEffect, useRef } from 'react';
import { Send, Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

function SupportDemo() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);

  const WEBHOOK_URL = 'https://n8n.notiontech.io/webhook/ac669958-eecc-44cb-a228-806ec2e05cc5/chat';

  // Generate or retrieve session ID
  useEffect(() => {
    const generateSessionId = () => {
      return Math.random().toString(36).substring(2, 15) + 
             Math.random().toString(36).substring(2, 15) +
             Date.now().toString(36);
    };

    const storedSessionId = sessionStorage.getItem('support-session-id');
    
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = generateSessionId();
      sessionStorage.setItem('support-session-id', newSessionId);
      setSessionId(newSessionId);
    }
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isLoading || !sessionId) return;

    const userMessage = {
      id: Date.now(),
      text: message,
      isUser: true
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionId,
          action: 'sendMessage',
          chatInput: message
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const botMessage = {
        id: Date.now() + 1,
        text: data.output || data.response || data.message || (typeof data === 'string' ? data : JSON.stringify(data)),
        isUser: false
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Failed to send message. Please try again.',
        isUser: false,
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  return (
    <div className="quickbot-page">
      <div className="quickbot-container">
        <div className="quickbot-chat-area" ref={chatContainerRef}>
          <div className="quickbot-header">
            <h1>Support Chat Bot</h1>
            <p>Get instant help with your technical questions</p>
          </div>
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="empty-state">
                <Zap size={48} className="empty-icon" />
                <h3>Start a conversation</h3>
                <p>Type your message below to begin</p>
              </div>
            )}

            {messages.map((message) => (
              <div key={message.id} className={`chat-message ${message.isUser ? 'user' : 'bot'}`}>
                <div className={`message-content ${message.isError ? 'error' : ''}`}>
                  {message.isUser ? (
                    message.text
                  ) : (
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="chat-message bot">
                <div className="message-content">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="quickbot-input-area">
          <div className="input-wrapper">
            <input
              ref={textareaRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              disabled={isLoading}
              className="chat-input"
            />
            <button 
              onClick={sendMessage} 
              disabled={isLoading || !inputMessage.trim()}
              className={`send-btn ${inputMessage.trim() ? 'active' : ''}`}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .quickbot-page {
          height: calc(100vh - 80px);
          margin-top: 80px;
          background: var(--soft-gray);
          display: flex;
          flex-direction: column;
        }

        .quickbot-container {
          max-width: 1400px;
          width: 100%;
          margin: 0 auto;
          padding: 2rem 4rem;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .quickbot-chat-area {
          background: white;
          border-radius: 12px;
          border: 1px solid var(--light-gray);
          flex: 1;
          overflow-y: auto;
          margin-bottom: 2rem;
          scroll-behavior: smooth;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
        }

        .quickbot-header {
          text-align: center;
          padding: 2rem 3rem 1rem 3rem;
          border-bottom: 1px solid var(--light-gray);
          margin-bottom: 2rem;
          flex-shrink: 0;
        }

        .quickbot-header h1 {
          font-size: 2.25rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.75rem;
          letter-spacing: -0.02em;
        }

        .quickbot-header p {
          font-size: 1.125rem;
          color: var(--text-secondary);
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--text-muted);
        }

        .empty-icon {
          color: var(--primary-blue);
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .empty-state p {
          color: var(--text-secondary);
        }

        .chat-messages {
          flex: 1;
          padding: 0 3rem 2rem 3rem;
        }

        .chat-message {
          margin-bottom: 2rem;
          display: block;
          width: 100%;
        }

        .message-content {
          width: 100%;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          line-height: 1.7;
          font-size: 1rem;
          margin-bottom: 0.75rem;
        }

        .user .message-content {
          background: linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%);
          color: white;
          text-align: right;
        }

        .bot .message-content {
          background: var(--soft-gray);
          color: var(--text-primary);
          border: 1px solid var(--light-gray);
          text-align: left;
        }

        .bot .message-content h1,
        .bot .message-content h2,
        .bot .message-content h3 {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .bot .message-content p {
          margin: 0.5rem 0;
        }

        .bot .message-content ul,
        .bot .message-content ol {
          margin: 0.5rem 0;
          padding-left: 1.25rem;
        }

        .bot .message-content li {
          margin: 0.25rem 0;
        }

        .bot .message-content code {
          background: var(--soft-gray);
          padding: 0.125rem 0.25rem;
          border-radius: 3px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.875rem;
        }

        .bot .message-content pre {
          background: var(--soft-gray);
          padding: 0.75rem;
          border-radius: 6px;
          overflow-x: auto;
          margin: 0.5rem 0;
        }

        .bot .message-content pre code {
          background: transparent;
          padding: 0;
        }

        .bot .message-content strong {
          font-weight: 600;
        }

        .bot .message-content em {
          font-style: italic;
        }

        .message-content.error {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .typing-dots {
          display: flex;
          gap: 4px;
          padding: 0.25rem 0;
        }

        .typing-dots span {
          width: 8px;
          height: 8px;
          background: var(--muted-gray);
          border-radius: 50%;
          animation: typing 1.4s infinite;
        }

        .typing-dots span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-dots span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.5;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        .quickbot-input-area {
          background: white;
          border-radius: 20px;
          border: 1px solid var(--light-gray);
          padding: 0.5rem 1rem;
          box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.08);
        }

        .input-wrapper {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          background: var(--soft-gray);
          border-radius: 16px;
          padding: 0.5rem 1rem;
          border: 2px solid transparent;
          transition: all 0.2s ease;
          min-height: 40px;
        }

        .input-wrapper:focus-within {
          border-color: var(--primary-blue);
          background: white;
          box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.1);
        }

        .chat-input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: none;
          background: transparent;
          font-size: 1rem;
          color: var(--text-primary);
          outline: none;
          resize: none;
          min-height: 20px;
        }

        .chat-input::placeholder {
          color: var(--text-muted);
        }

        .chat-input:disabled {
          cursor: not-allowed;
        }

        .send-btn {
          background: linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%);
          color: white;
          border: none;
          border-radius: 4px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(74, 144, 226, 0.3);
          min-height: 32px;
          min-width: 32px;
        }

        .send-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
        }

        .send-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .send-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        @media (max-width: 768px) {
          .quickbot-container {
            padding: 1rem 2rem;
          }

          .quickbot-header {
            padding: 1rem 0 1.5rem 0;
          }

          .quickbot-header h1 {
            font-size: 2rem;
          }

          .quickbot-chat-area {
            padding: 3rem 2rem;
            margin-bottom: 1.5rem;
          }

          .message-content {
            padding: 1.25rem 1.5rem;
          }

          .quickbot-input-area {
            padding: 1.25rem 1.5rem;
          }

          .input-wrapper {
            min-height: 52px;
          }

          .send-btn {
            padding: 0.875rem 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default SupportDemo;